import { useEffect, useState } from 'react';

import { overlayPfad, readBase64, readText } from '../../platform/storage';
import { Icon, type IconName } from '../../ui/Icon';
import styles from './CommentSheet.module.css';

/**
 * Vorschau einer abgelegten Datei — **mit** ihren Markierungen.
 *
 * Das Bild wird beim Anzeigen aus dem Documents-Ordner nachgeladen. Es liegt bewusst nicht
 * im Store: ein Base64-Bild dort würde bei jedem Tastendruck mitkopiert und den Zustand
 * um Megabyte aufblähen.
 *
 * ⚠️ **Die Markierebene ist eine eigene Datei** (`<bild>-overlay.svg`, s. `annotateSave.ts`) —
 * das Foto selbst bleibt unberührt, damit die Beglaubigung stimmt und sich jede Marke wieder
 * ändern lässt. Genau deshalb zeigte die Vorschau bis zum 2026-09-13 ein **unmarkiertes** Bild:
 * wer drei Fotos derselben Wunde aufgenommen und eines beschriftet hatte, sah dreimal dasselbe
 * (Befund des Nutzers). Die Ebene wird hier zweitens geladen und deckungsgleich darübergelegt.
 *
 * Ohne Datei bleibt ein erkennbarer Platzhalter stehen — kein leeres Feld, das wie ein
 * Ladefehler aussieht.
 */
export function PhotoThumb({
  path,
  alt,
  platzhalter = 'camera',
  stand = 0,
  ebeneSvg,
}: {
  path?: string;
  alt: string;
  /** Was steht, solange es kein Bild gibt. Die Videoliste zeigt hier ihr eigenes Zeichen. */
  platzhalter?: IconName;
  /**
   * Zähler, der die Markierebene neu lesen lässt.
   *
   * ⚠️ **Ohne ihn zeigte die Kachel die frische Markierung nicht** (Befund des Nutzers,
   * 2026-09-17). Das Markierblatt liegt in `MediaTabs` als **Geschwister** der Kachel, nicht
   * über ihr: die Kachel bleibt beim Markieren gemountet, ihr `path` ändert sich nicht, und
   * ein Effekt an `[path]` läuft kein zweites Mal. Die eben geschriebene `-overlay.svg` wurde
   * deshalb nie nachgelesen — der Prüfer sah sein unmarkiertes Foto und hielt die Arbeit für
   * verloren.
   */
  stand?: number;
  /**
   * Eine **fertige** Ebene, die nicht neben der Datei liegt.
   *
   * ⚠️ **Für die Videokachel** (`VideoThumb`): deren Ebene ist zeitabhängiges JSON, und das
   * Standbild trägt nur einen Augenblick daraus. Es gibt also nichts zu lesen — das SVG wird
   * gerechnet und hier hereingereicht.
   *
   * Ist es gesetzt, wird **nicht** nach `<pfad>-overlay.svg` gesucht: neben einem
   * `<video>-thumb.jpg` liegt so eine Datei nie, und der Griff ins Leere stünde nur im
   * Dateilog. `undefined` heisst „lies selbst", nicht „zeige nichts".
   */
  ebeneSvg?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [ebene, setEbene] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!path) return;
    let alive = true;
    readBase64(path)
      .then((data) => {
        if (!alive) return;
        if (data) setSrc('data:image/jpeg;base64,' + data);
        else setFailed(true);
      })
      .catch(() => alive && setFailed(true));

    return () => {
      alive = false;
    };
  }, [path]);

  /*
   * ⚠️ **Die Ebene hat ihren eigenen Effekt**, und zwar wegen des `stand` in der Abhängigkeit:
   * das Foto liegt unverändert da, ein zweites `readBase64` holte mehrere Megabyte durch den
   * Base64-Umweg ohne Not. Nur die Ebene wird neu gelesen.
   */
  useEffect(() => {
    if (!path || ebeneSvg !== undefined) return;
    let alive = true;

    // Die Ebene ist der Zugabe-Teil: gibt es keine, bleibt es beim Bild. Ein Fehlschlag hier
    // darf die Vorschau nicht als kaputt erscheinen lassen — das Foto ist ja da.
    readText(overlayPfad(path))
      .then((svg) => {
        if (!alive) return;
        // Leer heisst: die Markierungen wurden geräumt. Dann muss auch die angezeigte Ebene
        // weg — sonst bliebe sie über dem Bild stehen, obwohl die Datei gelöscht ist.
        if (!svg.includes('<svg')) {
          setEbene(null);
          return;
        }
        // Als Datenadresse statt als eingefügtes Markup: hier kommt eine Datei aus dem
        // Dateisystem, und die gehört nicht ungeprüft in den DOM.
        setEbene('data:image/svg+xml;utf8,' + encodeURIComponent(svg));
      })
      .catch(() => undefined);

    return () => {
      alive = false;
    };
  }, [path, stand, ebeneSvg]);

  /*
   * Die hereingereichte Ebene schlaegt die gelesene. Beide zugleich gibt es nicht: ist
   * `ebeneSvg` gesetzt, laeuft der Lesevorgang oben gar nicht erst.
   */
  const gezeigteEbene =
    ebeneSvg !== undefined ? 'data:image/svg+xml;utf8,' + encodeURIComponent(ebeneSvg) : ebene;

  if (src) {
    return (
      <span className={styles.thumbStapel}>
        <img className={styles.thumbImage} src={src} alt={alt} />
        {/* `object-fit: cover` auf beiden: die Ebene trägt dasselbe Seitenverhältnis wie das
            Bild (ihr `viewBox` kommt aus dessen Grösse), also schneiden beide gleich ab. */}
        {gezeigteEbene && (
          <img className={styles.thumbEbene} src={gezeigteEbene} alt="" aria-hidden="true" />
        )}
      </span>
    );
  }

  return (
    <span className={styles.placeholder}>
      <Icon name={failed || !path ? platzhalter : 'clock'} size={22} />
    </span>
  );
}
